import merge from 'lodash/fp/merge';
import { fallbackTheme } from './FallbackTheme/fallback.js';

class Theme {
  constructor() {
    let {
      borderRadius = {},
      breakPoints = {},
      iconSize = {},
      palette = {},
      spacingBase,
      typography = {},
      card = {},
      elevations = {},
      grid = {},
      fontFaces,
      defaultFontFamily,
      components = {}
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this.borderRadius = merge(fallbackTheme.borderRadius, borderRadius);
    this.breakPoints = merge(fallbackTheme.breakPoints, breakPoints);
    this.iconSize = merge(fallbackTheme.iconSize, iconSize);
    this.palette = merge(fallbackTheme.palette, palette);
    // Adds backwards compatibility with old grays naming
    this.palette.gray = mergeGrays(fallbackTheme.palette.gray, palette.gray);
    this.spacingBase = spacingBase || fallbackTheme.spacingBase;
    this.typography = merge(fallbackTheme.typography, typography);
    this.card = merge(fallbackTheme.card, card);
    this.elevations = merge(fallbackTheme.elevations, elevations);
    this.grid = merge(fallbackTheme.grid, grid);
    this.fontFaces = fontFaces || fallbackTheme.fontFaces;
    this.defaultFontFamily = defaultFontFamily || fallbackTheme.defaultFontFamily;
    this.components = components;
  }
  removePx = px => parseInt(px.substring(0, px.indexOf('px')));
  mediaQuery = breakpoint => `@media (min-width: ${this.breakPoints[breakpoint]}px)`;

  /*
   * @deprecated Please use getComponentOverride. Types may not be accurate if used outside @packlink/brands
   * We switched to allow partial component overrides, so this styling may be incomplete
   */
  getComponent = component => this.components[component] || {};
  getComponentOverride = component => {
    return this.components[component] || {};
  };
  spacing = (() => {
    var _this = this;
    return function () {
      let multiplier = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
      return _this.spacingBase * multiplier;
    };
  })();
  getElevation = elevation => ({
    boxShadow: this.elevations[elevation].boxShadow,
    zIndex: this.elevations[elevation].zIndex
  });
  getCardStyle = () => ({
    backgroundColor: this.palette.white,
    borderRadius: this.card.borderRadius.mobile,
    [this.mediaQuery('tablet')]: {
      borderRadius: this.card.borderRadius.tablet
    },
    [this.mediaQuery('desktop')]: {
      borderRadius: this.card.borderRadius.desktop
    }
  });
  toRem = (() => {
    var _this2 = this;
    return function (fontSizePx) {
      let baseFontSizePx = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : _this2.typography.fontSize.M;
      return `${_this2.removePx(fontSizePx) / _this2.removePx(baseFontSizePx)}rem`;
    };
  })();
  toEm = percentage => {
    return typeof percentage === 'number' ? `${percentage / 100}em` : percentage;
  };
  toPercentage = value => `${value * 100}%`;
  toRGBA = (hexCode, alpha) => {
    let hex = hexCode.replace('#', '');
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const red = parseInt(hex.substring(0, 2), 16),
      green = parseInt(hex.substring(2, 4), 16),
      blue = parseInt(hex.substring(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };
  getLineHeight = (fontSizePx, lineHeightPx) => this.removePx(lineHeightPx) / this.removePx(fontSizePx);
}
function mergeGrays(fallbackGray, gray) {
  return {
    ...fallbackGray,
    ...gray,
    50: (gray === null || gray === void 0 ? void 0 : gray[50]) || (gray === null || gray === void 0 ? void 0 : gray.megaLight) || fallbackGray.megaLight,
    100: (gray === null || gray === void 0 ? void 0 : gray[100]) || (gray === null || gray === void 0 ? void 0 : gray.ultraLight) || fallbackGray.ultraLight,
    200: (gray === null || gray === void 0 ? void 0 : gray[200]) || (gray === null || gray === void 0 ? void 0 : gray.light) || fallbackGray.light,
    600: (gray === null || gray === void 0 ? void 0 : gray[600]) || (gray === null || gray === void 0 ? void 0 : gray.main) || fallbackGray.main,
    900: (gray === null || gray === void 0 ? void 0 : gray[900]) || (gray === null || gray === void 0 ? void 0 : gray.dark) || fallbackGray.dark
  };
}

export { Theme };
//# sourceMappingURL=Theme.js.map
